import { useEffect, useRef, useState, type ReactNode } from "react";
import Icon from "../Icon";
import { MaterialNames } from "../material_symbol_names";
import classNames from "classnames";
import Avatar from "../Avatar";
import { PhoneNumber } from "@/types";

import Checkbox from "../Checkbox";
import CopyButton from "../CopyButton";
import IconButton from "../IconButton";
import Tooltip from "../Tooltip";
import Tag, { TagType, TagStyle } from "../Tag/Tag";

/**
 * Primary trailing action: `action` — makes the row clickable and shows a trailing chevron icon.
 * Used for navigation (e.g. clicking a search result to go to a detail page).
 *
 * Primary trailing selection: `useCheckbox` — renders a trailing checkbox with interactive row styles.
 * Used for selection (e.g. picking items from a list).
 *
 * `action` and `useCheckbox` are mutually exclusive. When both are set, `useCheckbox` takes precedence.
 *
 * Secondary trailing action: `secondaryTrailingAction` — optional IconButton rendered immediately left of
 * the primary trailing control (checkbox or chevron). Combined with `useCheckbox`, the row uses `role="group"`.
 */
export interface ListItemProps {
  className?: string;
  iconName?: MaterialNames;
  iconBackground?: boolean;
  picture?: string;
  useAvatar?: boolean;
  compact?: boolean;
  overline?: string;
  headline?: string | JSX.Element | null;
  supportingText?: string | JSX.Element | null;
  trailingSupportingText?: string | null;
  action?: () => void;
  actionIcon?: MaterialNames;
  secondaryTrailingAction?: () => void;
  secondaryTrailingActionIcon?: MaterialNames;
  secondaryTrailingActionAriaLabel?: string;
  secondaryTrailingActionTestId?: string;
  renderActions?: () => JSX.Element;
  status?: () => JSX.Element;
  leading?: JSX.Element;
  copyable?: boolean;
  copyText?: string;
  /** Analytics-safe field name for the copy control (defaults to `overline`). */
  copyFieldLabel?: string;
  tooltipTitle?: string;
  tooltipSupportingText?: string;
  tagText?: string | JSX.Element;
  tagType?: TagType;
  tagStyle?: TagStyle;
  tagIconName?: MaterialNames;
  tagTooltipText?: string;
  tagTestId?: string;
  tagPosition?: "inline" | "trailing";
  longContent?: boolean;
  centerLeadingIcon?: boolean;
  linked?: boolean;
  useCheckbox?: boolean;
  checkboxChecked?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  checkboxName?: string;
  isUpdated?: boolean;
}

export interface IconItemProps {
  name: MaterialNames;
  label: string | undefined;
}

export interface MetricListItemProps {
  iconName: MaterialNames;
  overline: string;
  headline: string | JSX.Element | null;
  tooltipTitle?: string;
  tooltipSupportingText?: string;
}

export interface MetricListItemGridProps {
  children: ReactNode;
  className?: string;
  testId?: string;
}

export default function ListItem({
  className,
  iconName,
  iconBackground,
  overline,
  headline,
  supportingText,
  trailingSupportingText,
  action,
  actionIcon = "navigate_next",
  secondaryTrailingAction,
  secondaryTrailingActionIcon = "edit",
  secondaryTrailingActionAriaLabel,
  secondaryTrailingActionTestId,
  picture,
  useAvatar: useAvatarProp = false,
  compact = false,
  renderActions,
  status,
  leading,
  copyable = false,
  copyText,
  copyFieldLabel,
  tooltipTitle,
  tooltipSupportingText,
  tagText,
  tagType,
  tagStyle = "strong",
  tagIconName,
  tagTooltipText,
  tagTestId,
  tagPosition = "inline",
  longContent = false,
  centerLeadingIcon = false,
  linked = false,
  useCheckbox = false,
  checkboxChecked = false,
  onCheckboxChange,
  checkboxName = "",
  isUpdated = false
}: ListItemProps) {
  if (isUpdated && !tagText) {
    tagText = "Updated";
    tagType = "warn";
    tagIconName = "error";
  }
  const [headlineHeight, setHeadlineHeight] = useState<number>(0);
  const [supportingTextHeight, setSupportingTextHeight] = useState<number>(0);
  const textRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const supportingTextRef = useRef<HTMLDivElement>(null);

  const useAvatar = useAvatarProp || !!picture;

  const hasAllThreeElements = !!(overline && headline && supportingText);

  useEffect(() => {
    if (headlineRef.current) {
      setHeadlineHeight(headlineRef.current.clientHeight);
    }
  }, [headline]);

  useEffect(() => {
    if (supportingTextRef.current) {
      setSupportingTextHeight(supportingTextRef.current.clientHeight);
    }
  }, [supportingText]);

  const isHeadlineMultiLine = headlineHeight > 24;
  const isSupportingTextMultiLine = supportingTextHeight > 24;
  const hasMultiLineContent = isHeadlineMultiLine || isSupportingTextMultiLine;
  const hasThreeLines = longContent || hasAllThreeElements || headlineHeight >= 56 || hasMultiLineContent;
  const heightClass = centerLeadingIcon || !hasThreeLines ? "items-center" : "items-start";

  // useCheckbox and action are mutually exclusive — useCheckbox takes precedence.
  const isActionRow = !!action && !useCheckbox;
  const hasSecondaryTrailingAction = !!secondaryTrailingAction;
  const isCheckboxWithSecondary = useCheckbox && hasSecondaryTrailingAction;
  const hasActionRowNestedInteractive =
    isActionRow &&
    ((copyable && !!copyText) || !!(tooltipTitle || tooltipSupportingText) || hasSecondaryTrailingAction);
  const rowUsesGroupRole = hasActionRowNestedInteractive || isCheckboxWithSecondary;
  const handleRowClick = useCheckbox ? () => onCheckboxChange?.(!checkboxChecked) : action;
  const showChevron = isActionRow && !renderActions;
  const showTrailingCluster =
    (copyable && !!copyText) ||
    (!!tagText && tagPosition === "trailing") ||
    hasSecondaryTrailingAction ||
    showChevron ||
    useCheckbox;
  const isSimpleCheckboxRow = useCheckbox && !hasSecondaryTrailingAction;

  const handleActionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      action?.();
    }
  };

  const handleKeyDown = isSimpleCheckboxRow
    ? (e: React.KeyboardEvent) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onCheckboxChange?.(!checkboxChecked);
        }
      }
    : isActionRow && !hasActionRowNestedInteractive
      ? handleActionKeyDown
      : undefined;

  const rowRole = isSimpleCheckboxRow ? "button" : rowUsesGroupRole ? "group" : isActionRow ? "button" : undefined;
  const rowTabIndex = isSimpleCheckboxRow || (isActionRow && !hasActionRowNestedInteractive) ? 0 : undefined;

  return (
    <div
      className={classNames("flex justify-between w-full min-w-0", compact ? "my-0" : "my-2", className, {
        "cursor-pointer": action || useCheckbox,
        "rounded-lg p-2 hover:bg-sys-brand-surface-container-high gap-1": useCheckbox
      })}
      data-testid={overline}
      onClick={handleRowClick}
      role={rowRole}
      tabIndex={rowTabIndex}
      onKeyDown={handleKeyDown}
    >
      <div className={classNames(`${heightClass} flex space-x-2 w-full min-w-0`)}>
        {leading}
        {useAvatar && <Avatar picture={picture} iconName={iconName} className="w-10 h-10" />}
        {!useAvatar && iconName && !picture && (
          <div
            className={classNames(
              "flex flex-shrink-0",
              iconBackground
                ? classNames(
                    "w-10 h-10 rounded-[6px] items-center justify-center mr-2",
                    linked ? "bg-extended-pgadarknavy-brand-color" : "bg-extended-pgasilver-brand-color"
                  )
                : centerLeadingIcon
                  ? "items-center"
                  : "items-start",
              { "h-full": hasThreeLines && !iconBackground && !centerLeadingIcon }
            )}
          >
            <Icon
              name={iconName}
              size={24}
              className={classNames(
                "flex",
                iconBackground
                  ? linked
                    ? "text-extended-pgadarknavy-brand-on-color"
                    : "text-extended-pgasilver-brand-on-color"
                  : "mx-2 text-sys-brand-on-surface-variant"
              )}
            />
          </div>
        )}
        <div
          ref={textRef}
          className={classNames("flex flex-col items-start min-w-0 flex-1", compact ? "gap-0" : "gap-1")}
        >
          {(overline || (tagText && tagPosition === "inline")) && (
            <div className="flex items-center gap-2">
              {overline && (
                <div
                  className={classNames(
                    "text-sys-brand-on-surface-variant",
                    compact ? "text-body-medium" : "text-label-large"
                  )}
                >
                  {overline}
                </div>
              )}
              {(tooltipTitle || tooltipSupportingText) && (
                <div onClick={(e) => e.stopPropagation()}>
                  <Tooltip
                    title={tooltipTitle}
                    supportingText={tooltipSupportingText}
                    variant={tooltipTitle ? "rich" : "plain"}
                    color={tooltipTitle ? "white" : undefined}
                    positioning="fixed"
                  >
                    <Icon
                      name="info"
                      size={20}
                      className="text-sys-brand-on-surface-variant cursor-help flex items-center justify-center"
                    />
                  </Tooltip>
                </div>
              )}
              {tagText && tagPosition === "inline" && (
                <Tag
                  tagType={tagType}
                  tagStyle={tagStyle}
                  size="small"
                  iconName={tagIconName}
                  tooltipText={tagTooltipText}
                  testId={tagTestId}
                >
                  {tagText}
                </Tag>
              )}
            </div>
          )}
          <div className={classNames("flex w-full min-w-0", hasThreeLines ? "items-start" : "items-center", "gap-2")}>
            <div
              ref={headlineRef}
              role={hasActionRowNestedInteractive ? "button" : undefined}
              tabIndex={hasActionRowNestedInteractive ? 0 : undefined}
              onKeyDown={hasActionRowNestedInteractive ? handleActionKeyDown : undefined}
              className={classNames(
                "break-words text-sys-brand-on-surface w-full min-w-0",
                "text-body-large-semibold",
                {
                  "opacity-20": !headline,
                  "whitespace-pre-line": longContent
                }
              )}
            >
              {headline || "--"}
            </div>
          </div>
          {supportingText && (
            <div
              ref={supportingTextRef}
              className={classNames(
                "text-sys-brand-on-surface-variant whitespace-pre-line break-words w-full min-w-0",
                compact ? "text-body-medium" : "text-body-large"
              )}
            >
              {supportingText}
            </div>
          )}
          {status && <div>{status()}</div>}
        </div>
      </div>
      {trailingSupportingText && (
        <div className="text-sys-brand-on-surface shrink-0 break-words text-label-medium">{trailingSupportingText}</div>
      )}
      {showTrailingCluster && (
        <div
          className={classNames("flex items-center gap-2 shrink-0 self-center", {
            "ml-auto": useCheckbox || hasSecondaryTrailingAction
          })}
        >
          {copyable && copyText && (
            <CopyButton
              textToCopy={copyText}
              copyFieldLabel={copyFieldLabel ?? overline ?? "Value"}
              className="flex-shrink-0"
            />
          )}
          {tagText && tagPosition === "trailing" && (
            <Tag
              tagType={tagType}
              tagStyle={tagStyle}
              size="small"
              iconName={tagIconName}
              tooltipText={tagTooltipText}
              testId={tagTestId}
            >
              {tagText}
            </Tag>
          )}
          {hasSecondaryTrailingAction && (
            <div onClick={(e) => e.stopPropagation()}>
              <IconButton
                name={secondaryTrailingActionIcon}
                ariaLabel={secondaryTrailingActionAriaLabel}
                title={secondaryTrailingActionAriaLabel}
                skipMargin
                data-testid={secondaryTrailingActionTestId}
                onClick={secondaryTrailingAction}
              />
            </div>
          )}
          {useCheckbox && (
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                name={checkboxName}
                checked={checkboxChecked}
                onChange={(val) => onCheckboxChange?.(val)}
                aria-label={typeof headline === "string" ? headline : checkboxName}
              />
            </div>
          )}
          {showChevron && <Icon className="flex justify-center" name={actionIcon} />}
        </div>
      )}
    </div>
  );
}

export function PhoneListItem({
  phone,
  icon,
  overline
}: {
  phone: PhoneNumber;
  icon: MaterialNames;
  overline: string;
}) {
  return (
    <ListItem
      iconName={icon}
      iconBackground
      overline={overline}
      copyable={!!phone.phoneFormatted}
      copyText={phone.phoneFormatted ?? undefined}
      {...(phone.phoneFormatted && {
        headline: (
          <a
            href={`tel:${phone.phoneNumber.replace(/\D/g, "")}`}
            className="text-sys-brand-on-surface hover:text-sys-brand-primary cursor-pointer text-body-large-semibold"
          >
            {phone.phoneFormatted}
          </a>
        )
      })}
    />
  );
}

export function IconItem({ name, label }: IconItemProps) {
  return (
    <div className="flex items-center gap-2">
      <Icon name={name} />
      <div className="text-body-large-semibold">{label}</div>
    </div>
  );
}

export function MetricListItem({
  iconName,
  overline,
  headline,
  tooltipTitle,
  tooltipSupportingText
}: MetricListItemProps) {
  return (
    <ListItem
      iconName={iconName}
      iconBackground
      longContent
      overline={overline}
      headline={headline}
      tooltipTitle={tooltipTitle}
      tooltipSupportingText={tooltipSupportingText}
    />
  );
}

export function MetricListItemGrid({ children, className, testId }: MetricListItemGridProps) {
  return (
    <div className={classNames("grid grid-cols-1 md:grid-cols-4 gap-4 items-start", className)} data-testid={testId}>
      {children}
    </div>
  );
}
